import { PageContainer } from "@/components/layout/PageContainer";
import { SplitView } from "@/components/layout/SplitView";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useTheme } from "@/app/providers/ThemeProvider";
import { useAuthStore } from "@/lib/store/authStore";
import { usePreferencesStore, type ExperienceStylePreference } from "@/lib/store/preferencesStore";
import { useNavigate } from "react-router-dom";
import { Moon, Sun, Database, Bell, Shield, LogOut, User, ListRestart, Receipt, Check, ChevronLeft } from "lucide-react";
import { CaretRight } from "@phosphor-icons/react";
import { useLanguage } from "@/i18n/useLanguage";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { UserProfilePage } from "@/features/users/UserProfilePage";
import { cn } from "@/lib/utils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cpResetDismissWidget } from "@/lib/api/controlPlaneOnboardingClient";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { ViewMode } from '@/lib/views/types';

const settingsCategories = [
  { id: "appearance", label: "Appearance", labelTh: "ธีมและการแสดงผล", icon: Sun, color: "bg-gradient-to-br from-yellow-500 to-orange-500" },
  { id: "api", label: "API Settings", labelTh: "การเชื่อมต่อ API", icon: Database, color: "bg-gradient-to-br from-blue-500 to-blue-600" },
  { id: "profile", label: "Profile", labelTh: "โปรไฟล์", icon: User, color: "bg-gradient-to-br from-green-500 to-green-600" },
  { id: "notifications", label: "Notifications", labelTh: "การแจ้งเตือน", icon: Bell, color: "bg-gradient-to-br from-purple-500 to-purple-600" },
  { id: "security", label: "Security", labelTh: "ความปลอดภัย", icon: Shield, color: "bg-gradient-to-br from-red-500 to-red-600" },
  { id: "billing", label: "Usage & Billing", labelTh: "แพ็กเกจและบิล", icon: Receipt, color: "bg-gradient-to-br from-emerald-500 to-teal-600", navigateTo: "/settings/usage" },
];

function ModalThumbnail() {
  return (
    <div className="relative w-14 h-10">
      <div className="absolute inset-0 rounded border border-border/60 bg-background" />
      <div className="absolute inset-x-1 inset-y-1 rounded bg-muted/60" />
      <div className="absolute left-2 top-1.5 right-2 bottom-1.5 rounded border border-border bg-background shadow-sm flex flex-col gap-0.5 p-1">
        <div className="h-1 w-6 rounded-full bg-muted-foreground/30" />
        <div className="h-1 w-4 rounded-full bg-muted-foreground/20" />
      </div>
    </div>
  );
}

function InlineThumbnail() {
  const row = (w: string) => (
    <div className="flex items-center gap-1">
      <div className={cn("h-1 rounded-full bg-muted-foreground/30", w)} />
      <div className="h-1 flex-1 rounded-full bg-primary/20 border-b border-dashed border-primary/40" />
    </div>
  );
  return (
    <div className="w-14 h-10 rounded border border-border/60 bg-background flex flex-col gap-1 p-1.5">
      {row("w-4")}{row("w-3")}{row("w-5")}
    </div>
  );
}

export function SettingsPage() {
  const { theme, toggleTheme } = useTheme();
  const [selectedCategory, setSelectedCategory] = useState("appearance");
  const [mobileShowContent, setMobileShowContent] = useState(false);
  const frappeUrl = useAuthStore((state) => state.frappeUrl);
  const username = useAuthStore((state) => state.username);
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  const fieldLayoutStyle = usePreferencesStore((state) => state.fieldLayoutStyle);
  const setFieldLayoutStyle = usePreferencesStore((state) => state.setFieldLayoutStyle);
  const devMode = usePreferencesStore((state) => state.devMode);
  const viewMode = usePreferencesStore((state) => state.viewMode);
  const setViewMode = usePreferencesStore((state) => state.setViewMode);
  const experienceStyle = usePreferencesStore((state) => state.experienceStyle);
  const setExperienceStyle = usePreferencesStore((state) => state.setExperienceStyle);
  const detailEditMode = usePreferencesStore((state) => state.detailEditMode);
  const setDetailEditMode = usePreferencesStore((state) => state.setDetailEditMode);
  const { language, setLanguage } = useLanguage();
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const handleCategoryClick = (categoryId: string) => {
    const cat = settingsCategories.find((c) => c.id === categoryId);
    if (cat?.navigateTo) {
      navigate(cat.navigateTo);
      return;
    }
    setSelectedCategory(categoryId);
    setMobileShowContent(true);
  };

  const queryClient = useQueryClient();
  const currentTenant = useAuthStore((state) => state.currentTenant);

  const handleLogout = async () => {
    queryClient.clear();
    await logout();
    navigate("/login");
  };

  const handleResetOnboarding = async () => {
    try {
      await cpResetDismissWidget();
    } catch {
      // Best effort
    }
    if (currentTenant) {
      localStorage.removeItem(`zvg_onboarding_dismissed_${currentTenant}`);
      localStorage.removeItem(`zvg_onboarding_seen_${currentTenant}`);
    }
    queryClient.invalidateQueries({ queryKey: ["onboarding-missions"] });
    navigate("/");
  };

  const selectedCat = settingsCategories.find((c) => c.id === selectedCategory);

  // ── Settings content (shared between mobile & desktop) ──
  const settingsContent = (
    <div className="space-y-6">
      {selectedCategory === "appearance" && (
        <div className="space-y-6">
          {isDesktop && (
            <div>
              <h2 className="text-2xl font-semibold mb-2">Appearance</h2>
              <p className="text-muted-foreground">Customize the look and feel of the application</p>
            </div>
          )}
          <SettingsSection title="Theme">
            <SettingsRow label="Dark Mode" description="Switch between light and dark themes">
              <Button variant="outline" size="icon" onClick={toggleTheme} className="rounded-md shrink-0">
                {theme === "dark" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </Button>
            </SettingsRow>
          </SettingsSection>
          <SettingsSection title="Language">
            <SettingsRow label="Display Language" description="Choose your preferred language">
              <Select value={language} onValueChange={(value) => setLanguage(value as "en" | "th")}>
                <SelectTrigger className="w-[140px] md:w-[180px] rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="th">ไทย</SelectItem>
                </SelectContent>
              </Select>
            </SettingsRow>
          </SettingsSection>
          <SettingsSection title="Field Layout">
            <SettingsRow label="Field Layout Style" description="Choose how fields are displayed">
              <Select value={fieldLayoutStyle} onValueChange={(value) => setFieldLayoutStyle(value as 'apple' | 'grid')}>
                <SelectTrigger className="w-[140px] md:w-[180px] rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="apple">Apple Style</SelectItem>
                  <SelectItem value="grid">Grid Layout</SelectItem>
                </SelectContent>
              </Select>
            </SettingsRow>
          </SettingsSection>
          <SettingsSection title="Editing Style">
            <Card className="rounded-2xl border-0">
              <CardContent className="p-4 space-y-3">
                <p className="text-sm text-muted-foreground">Edit fields in detail pages</p>
                <div className="grid grid-cols-2 gap-3">
                  {(["modal", "inline"] as const).map((mode) => {
                    const isActive = detailEditMode === mode;
                    return (
                      <button key={mode} type="button" onClick={() => setDetailEditMode(mode)}
                        className={cn("relative flex flex-col items-center gap-2 rounded-xl p-4 text-left transition-all",
                          isActive ? "border-2 border-primary shadow-apple-sm" : "border border-border hover:border-muted-foreground/40"
                        )}>
                        <div className="w-full h-16 rounded-lg bg-muted/50 flex items-center justify-center">
                          {mode === "modal" ? <ModalThumbnail /> : <InlineThumbnail />}
                        </div>
                        <span className="text-sm font-medium capitalize">{mode}</span>
                        {isActive && <Check className="absolute top-2 right-2 w-4 h-4 text-primary" />}
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground">
                  {detailEditMode === "modal" ? "Click a section to open an edit dialog" : "Edit fields directly on the page"}
                </p>
              </CardContent>
            </Card>
          </SettingsSection>
          {devMode && (
            <SettingsSection title="View Mode">
              <SettingsRow label="View Mode" description="Switch between System and Experience views">
                <Select value={viewMode} onValueChange={(value) => setViewMode(value as ViewMode)}>
                  <SelectTrigger className="w-[140px] md:w-[180px] rounded-md"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="experience">Experience</SelectItem>
                    <SelectItem value="system">System</SelectItem>
                  </SelectContent>
                </Select>
              </SettingsRow>
            </SettingsSection>
          )}
          <SettingsSection title="Experience View Style">
            <SettingsRow label="Experience View Style" description={viewMode === 'system' ? 'Only applies when Experience mode is enabled' : 'Choose the layout style'}>
              <Select value={experienceStyle} onValueChange={(value) => setExperienceStyle(value as ExperienceStylePreference)}>
                <SelectTrigger className="w-[140px] md:w-[200px] rounded-md"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto</SelectItem>
                  <SelectItem value="shopify">Shopify</SelectItem>
                  <SelectItem value="woocommerce">WooCommerce</SelectItem>
                  <SelectItem value="erpnext">ERPNext</SelectItem>
                  <SelectItem value="apple">Apple</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </SettingsRow>
          </SettingsSection>
        </div>
      )}

      {selectedCategory === "api" && (
        <div className="space-y-6">
          {isDesktop && (
            <div>
              <h2 className="text-2xl font-semibold mb-2">API Settings</h2>
              <p className="text-muted-foreground">Configure your API connection</p>
            </div>
          )}
          <SettingsSection title="Connection">
            <Card className="rounded-2xl border-0">
              <CardContent className="p-4 space-y-4">
                <div><label className="text-sm font-medium text-muted-foreground">Frappe Instance URL</label><p className="mt-1 text-sm font-mono">{frappeUrl || "Not connected"}</p></div>
                <div><label className="text-sm font-medium text-muted-foreground">Logged in as</label><p className="mt-1 text-sm">{username || "Not logged in"}</p></div>
                <div><label className="text-sm font-medium text-muted-foreground">Authentication Method</label><p className="mt-1 text-sm">Session-based</p></div>
              </CardContent>
            </Card>
          </SettingsSection>
        </div>
      )}

      {selectedCategory === "notifications" && (
        <div className="space-y-6">
          {isDesktop && (
            <div>
              <h2 className="text-2xl font-semibold mb-2">Notifications</h2>
              <p className="text-muted-foreground">Manage your notification preferences</p>
            </div>
          )}
          <SettingsSection title="Preferences">
            <Card className="rounded-2xl border-0">
              <CardContent className="p-4">
                <p className="text-sm text-muted-foreground">Notification settings coming soon</p>
              </CardContent>
            </Card>
          </SettingsSection>
        </div>
      )}

      {selectedCategory === "profile" && (
        <div className="space-y-6">
          <UserProfilePage />
          <SettingsSection title="Onboarding">
            <SettingsRow label="Setup Progress" description="Re-open the onboarding widget">
              <Button variant="outline" className="rounded-md shrink-0" onClick={handleResetOnboarding}>
                <ListRestart className="w-4 h-4 mr-2" />Show Widget
              </Button>
            </SettingsRow>
          </SettingsSection>
        </div>
      )}

      {selectedCategory === "security" && (
        <div className="space-y-6">
          {isDesktop && (
            <div>
              <h2 className="text-2xl font-semibold mb-2">Security</h2>
              <p className="text-muted-foreground">Manage your security settings</p>
            </div>
          )}
          <SettingsSection title="Session">
            <Card className="rounded-2xl border-0">
              <CardContent className="p-4 space-y-4">
                <p className="text-sm text-muted-foreground">You are currently logged in. Log out to switch accounts.</p>
                <Button variant="destructive" onClick={handleLogout} className="w-full sm:w-auto">
                  <LogOut className="w-4 h-4 mr-2" />Log Out
                </Button>
              </CardContent>
            </Card>
          </SettingsSection>
        </div>
      )}
    </div>
  );

  // ── Mobile layout: list → drill-down ──
  if (!isDesktop) {
    if (mobileShowContent) {
      return (
        <div className="min-h-screen bg-gray-50 dark:bg-background">
          {/* Back header */}
          <div className="sticky top-0 z-10 flex items-center gap-2 h-12 px-3 bg-white dark:bg-gray-950 border-b border-border/40">
            <button type="button" onClick={() => setMobileShowContent(false)} className="flex items-center gap-1 text-primary text-sm font-medium">
              <ChevronLeft className="w-5 h-5" />
              ตั้งค่า
            </button>
            <span className="flex-1 text-center text-sm font-semibold pr-12">{selectedCat?.labelTh ?? selectedCat?.label}</span>
          </div>
          <div className="px-4 py-4">
            {settingsContent}
          </div>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-gray-50 dark:bg-background">
        <div className="bg-white dark:bg-gray-950 px-5 pt-4 pb-3">
          <h1 className="text-lg font-semibold text-foreground">ตั้งค่า</h1>
        </div>
        <div className="bg-white dark:bg-gray-950 mt-2">
          {settingsCategories.map((cat) => {
            const Icon = cat.icon;
            return (
              <button
                key={cat.id}
                type="button"
                onClick={() => handleCategoryClick(cat.id)}
                className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-gray-50 dark:active:bg-gray-900 transition-colors"
              >
                <div className={cn("w-8 h-8 rounded-lg flex items-center justify-center shrink-0", cat.color)}>
                  <Icon className="w-4 h-4 text-white" />
                </div>
                <span className="flex-1 text-sm text-foreground text-left">{cat.labelTh ?? cat.label}</span>
                <CaretRight size={16} className="text-gray-300 shrink-0" />
              </button>
            );
          })}
        </div>
        {/* Logout */}
        <div className="bg-white dark:bg-gray-950 mt-2">
          <button type="button" onClick={handleLogout} className="w-full flex items-center gap-3 px-5 py-3.5 active:bg-gray-50 dark:active:bg-gray-900 transition-colors">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 bg-red-500/10">
              <LogOut className="w-4 h-4 text-red-500" />
            </div>
            <span className="flex-1 text-sm text-red-500 text-left">ออกจากระบบ</span>
          </button>
        </div>
      </div>
    );
  }

  // ── Desktop layout: SplitView (unchanged) ──
  const leftContent = (
    <div className="h-full flex flex-col">
      <div className="p-6 pb-4">
        <h2 className="text-lg font-semibold">Settings</h2>
      </div>
      <div className="flex-1 overflow-y-auto px-6 pb-6 space-y-3">
        {settingsCategories.filter(c => !c.navigateTo).map((category) => {
          const Icon = category.icon;
          const isActive = selectedCategory === category.id;
          return (
            <Button
              key={category.id}
              variant="ghost"
              className={cn(
                "w-full justify-start gap-3 px-2 py-3 rounded transition-all duration-300",
                "hover:scale-[1.02] active:scale-[0.98]",
                isActive
                  ? "bg-white/60 dark:bg-gray-800/60 text-gray-900 dark:text-gray-100 shadow-apple"
                  : "text-gray-700 dark:text-gray-300 hover:bg-white/40 dark:hover:bg-gray-800/30"
              )}
              onClick={() => handleCategoryClick(category.id)}
            >
              <div className={cn("w-6 h-6 rounded-sm flex items-center justify-center shrink-0 transition-all duration-300", category.color, isActive ? "scale-105 shadow-md" : "scale-100")}>
                <Icon className="w-3 h-3 text-white" />
              </div>
              {category.label}
            </Button>
          );
        })}
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 px-2 py-3 rounded transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] text-gray-700 dark:text-gray-300 hover:bg-white/40 dark:hover:bg-gray-800/30"
          onClick={() => navigate("/settings/usage")}
        >
          <div className="w-6 h-6 rounded-sm flex items-center justify-center shrink-0 bg-gradient-to-br from-emerald-500 to-teal-600">
            <Receipt className="w-3 h-3 text-white" />
          </div>
          Usage & Billing
        </Button>
      </div>
    </div>
  );

  const rightContent = (
    <div className="p-6">
      {settingsContent}
    </div>
  );

  return (
    <PageContainer>
      <div className="min-h-screen py-6">
        <SplitView left={leftContent} right={rightContent} leftClassName="!w-64" />
      </div>
    </PageContainer>
  );
}

// ── Reusable settings primitives ──

function SettingsSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide pl-4">{title}</h3>
      {children}
    </div>
  );
}

function SettingsRow({ label, description, children }: { label: string; description?: string; children: React.ReactNode }) {
  return (
    <Card className="rounded-2xl border-0">
      <CardContent className="p-4">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-medium text-sm md:text-base">{label}</p>
            {description && <p className="text-xs md:text-sm text-muted-foreground">{description}</p>}
          </div>
          {children}
        </div>
      </CardContent>
    </Card>
  );
}
